import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAllBaskets, selectAllTopics } from '../../store/workspacesSlice';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';
import { resultItemKey } from './highlightMatch';
import ConnectionsResultRowList from './ConnectionsResultRowList';
import ConnectionsExpandedBasketCard from './ConnectionsExpandedBasketCard';
import ConnectionsExpandedStandaloneCard from './ConnectionsExpandedStandaloneCard';

const TOP_N = 2;

const buildResultEventProps = (item, tier) => ({
  result_kind: item.kind,
  tier,
  ...(item.kind === 'basket'
    ? { basket_id: item.id }
    : { topic_id: item.id }),
});

/**
 * Walk a tier: expanded cards inline; contiguous collapsed runs → one row list.
 */
const renderTierStack = ({
  items,
  expandedIds,
  searchTerm,
  onExpand,
  onCollapse,
  basketsById,
  topicsById,
  workspaceId,
  onTogglePin,
  onAddToBasket,
  onMoveToBasket,
  onTopicDeleted,
  onBeforeLeave,
  onAddItemToBasket,
  tier,
}) => {
  const nodes = [];
  let collapsedBuffer = [];

  const flushCollapsed = (keySuffix) => {
    if (collapsedBuffer.length === 0) return;
    nodes.push(
      <ConnectionsResultRowList
        key={`rows-${keySuffix}`}
        items={[...collapsedBuffer]}
        searchTerm={searchTerm}
        onExpand={(item) => onExpand(item, tier)}
      />
    );
    collapsedBuffer = [];
  };

  items.forEach((item, index) => {
    const key = resultItemKey(item);
    if (expandedIds.has(key)) {
      flushCollapsed(`${key}-before`);
      if (item.kind === 'basket') {
        nodes.push(
          <ConnectionsExpandedBasketCard
            key={`exp-${key}`}
            item={item}
            searchTerm={searchTerm}
            onCollapse={() => onCollapse(item, tier)}
            liveBasket={basketsById.get(item.id)}
            workspaceId={workspaceId}
            onTogglePin={onTogglePin}
            onMoveToBasket={onMoveToBasket}
            onTopicDeleted={onTopicDeleted}
            onBeforeLeave={onBeforeLeave}
            onAddItemToBasket={onAddItemToBasket}
          />
        );
      } else {
        nodes.push(
          <ConnectionsExpandedStandaloneCard
            key={`exp-${key}`}
            item={item}
            searchTerm={searchTerm}
            onCollapse={() => onCollapse(item, tier)}
            liveTopic={topicsById.get(item.id)}
            onAddToBasket={onAddToBasket}
            onMoveToBasket={onMoveToBasket}
            onTopicDeleted={onTopicDeleted}
            onBeforeLeave={onBeforeLeave}
          />
        );
      }
    } else {
      collapsedBuffer.push(item);
    }

    if (index === items.length - 1) {
      flushCollapsed(`${key}-end`);
    }
  });

  return nodes;
};

/**
 * Two-tier Connections search results (Name matches / Concept-only matches).
 * Top TOP_N of tier1 start expanded; tier2 starts fully collapsed.
 */
const ConnectionsSearchResults = ({
  tier1 = [],
  tier2 = [],
  searchTerm,
  workspaceId,
  onTogglePin,
  onAddToBasket,
  onMoveToBasket,
  onTopicDeleted,
  onBeforeLeave,
  onAddItemToBasket,
}) => {
  const baskets = useSelector(selectAllBaskets);
  const allTopics = useSelector(selectAllTopics);

  const basketsById = useMemo(() => {
    const map = new Map();
    (baskets || []).forEach((b) => map.set(b.id, b));
    return map;
  }, [baskets]);

  const topicsById = useMemo(() => {
    const map = new Map();
    (allTopics || []).forEach((t) => map.set(t.id, t));
    return map;
  }, [allTopics]);

  const tier1Signature = useMemo(
    () => tier1.map(resultItemKey).join('|'),
    [tier1]
  );

  const defaultExpandedKeysRef = useRef(new Set());

  const [expandedIds, setExpandedIds] = useState(() => {
    const ids = new Set();
    tier1.slice(0, TOP_N).forEach((item) => ids.add(resultItemKey(item)));
    defaultExpandedKeysRef.current = new Set(ids);
    return ids;
  });

  useEffect(() => {
    const ids = new Set();
    tier1.slice(0, TOP_N).forEach((item) => ids.add(resultItemKey(item)));
    defaultExpandedKeysRef.current = new Set(ids);
    setExpandedIds(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier1Signature]);

  const handleExpand = (item, tier) => {
    const key = resultItemKey(item);
    captureConnectionsEvent('connections_result_expanded', {
      ...buildResultEventProps(item, tier),
      is_default_expanded: defaultExpandedKeysRef.current.has(key),
    });
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const handleCollapse = (item, tier) => {
    captureConnectionsEvent('connections_result_collapsed', buildResultEventProps(item, tier));
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.delete(resultItemKey(item));
      return next;
    });
  };

  return (
    <div className="w-full max-w-[700px] space-y-8">
      {tier1.length > 0 && (
        <section aria-labelledby="connections-tier1-heading">
          <h2
            id="connections-tier1-heading"
            className="text-sm font-semibold text-neutral-700 mb-3"
          >
            Name matches
          </h2>
          <div className="space-y-3">
            {renderTierStack({
              items: tier1,
              expandedIds,
              searchTerm,
              onExpand: handleExpand,
              onCollapse: handleCollapse,
              basketsById,
              topicsById,
              workspaceId,
              onTogglePin,
              onAddToBasket,
              onMoveToBasket,
              onTopicDeleted,
              onBeforeLeave,
              onAddItemToBasket,
              tier: 1,
            })}
          </div>
        </section>
      )}

      {tier2.length > 0 && (
        <section aria-labelledby="connections-tier2-heading">
          <h2
            id="connections-tier2-heading"
            className="text-sm font-semibold text-neutral-700 mb-3"
          >
            Concept-only matches
          </h2>
          <div className="space-y-3">
            {renderTierStack({
              items: tier2,
              expandedIds,
              searchTerm,
              onExpand: handleExpand,
              onCollapse: handleCollapse,
              basketsById,
              topicsById,
              workspaceId,
              onTogglePin,
              onAddToBasket,
              onMoveToBasket,
              onTopicDeleted,
              onBeforeLeave,
              onAddItemToBasket,
              tier: 2,
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default ConnectionsSearchResults;
